import { filterSelectedKeywordsForSearchTerms } from './keyword-policy-client.js';
import { packKeywordsByBytes } from './byte-counter.js';
import { MAX_SEARCH_TERMS_BYTES } from './constants.js';

function normalizeKeyword(value) {
  return String(value || '').trim();
}

function normalizeKeywordList(values = []) {
  const seen = new Set();
  const result = [];
  for (const value of Array.isArray(values) ? values : []) {
    const keyword = normalizeKeyword(typeof value === 'object' && value
      ? (value.keyword || value.term || value.word || value.text || '')
      : value);
    const key = keyword.toLowerCase();
    if (!keyword || seen.has(key)) continue;
    seen.add(key);
    result.push(keyword);
  }
  return result;
}

const SEARCH_TERMS_POLICY_PRIORITY = Object.freeze({
  must_use: 0,
  backend_only: 1,
  can_use: 2,
});

function prioritizeByPolicy(keywords = [], keywordUsePolicies = []) {
  const policyLookup = new Map();
  for (const item of Array.isArray(keywordUsePolicies) ? keywordUsePolicies : []) {
    const keyword = normalizeKeyword(item?.keyword || item?.normalizedKeyword || '');
    const key = keyword.toLowerCase();
    if (!key || policyLookup.has(key)) continue;
    policyLookup.set(key, String(item?.policy || '').trim().toLowerCase());
  }
  return keywords
    .map((keyword, index) => ({
      keyword,
      index,
      priority: SEARCH_TERMS_POLICY_PRIORITY[policyLookup.get(keyword.toLowerCase())] ?? 99,
    }))
    .sort((left, right) => left.priority - right.priority || left.index - right.index)
    .map((item) => item.keyword);
}

function autoReviewApprovedKeys(autoSelectionReview = null) {
  if (!autoSelectionReview || typeof autoSelectionReview !== 'object') return null;
  // 降级时回退到宽松策略（不限制），让候选词正常被自动选中。
  // 之前返回空 Set 导致所有候选词被过滤掉，用户看到推荐词却一个都没选中。
  if (autoSelectionReview.status === 'degraded' || autoSelectionReview.degraded === true) return null;
  if (!['completed', 'partial'].includes(autoSelectionReview.status)) return null;
  const keys = new Set();
  for (const keyword of normalizeKeywordList(autoSelectionReview.approvedKeywords || [])) {
    keys.add(keyword.toLowerCase());
  }
  return keys;
}

export function buildAutoSelectedSearchTerms({
  seedKeywords = [],
  candidateKeywords = [],
  keywordUsePolicies = [],
  autoSelectionReview = null,
  approvedLimit = 40,
  targetBytes = 240,
} = {}) {
  const merged = [
    ...normalizeKeywordList(seedKeywords),
    ...normalizeKeywordList(candidateKeywords),
  ];
  const deduped = normalizeKeywordList(merged);
  const semanticApprovedKeys = autoReviewApprovedKeys(autoSelectionReview);
  const semanticEligible = semanticApprovedKeys === null
    ? deduped
    : deduped.filter((keyword) => semanticApprovedKeys.has(keyword.toLowerCase()));
  const allowed = prioritizeByPolicy(
    filterSelectedKeywordsForSearchTerms(semanticEligible, keywordUsePolicies, { operatorApproved: false }),
    keywordUsePolicies,
  );
  const requestedApprovedLimit = Number(approvedLimit);
  const normalizedApprovedLimit = Number.isFinite(requestedApprovedLimit) && requestedApprovedLimit > 0
    ? Math.floor(requestedApprovedLimit)
    : 40;
  const approvedKeywords = allowed.slice(0, normalizedApprovedLimit);
  const maxBytes = Math.min(
    MAX_SEARCH_TERMS_BYTES,
    Math.max(1, Number(targetBytes || 240))
  );
  const { packed, bytes } = packKeywordsByBytes(approvedKeywords, maxBytes);

  return {
    // “已选中”必须表示真正能进入 Search Terms 的词。候选池可以大于
    // Amazon 的字节上限，但不能把因超限被丢弃的词继续写进
    // selectedStKeywords，否则 Step02、生成输入和 Step04 关键词台账会
    // 对“使用了哪些词”给出彼此矛盾的答案。
    selected: packed,
    approvedKeywords,
    packedSearchTerms: packed,
    searchTermsEn: packed.join(' '),
    bytes,
    summary: {
      candidateCount: deduped.length,
      omittedBySemanticReview: Math.max(0, deduped.length - semanticEligible.length),
      eligibleCount: allowed.length,
      approvedCount: approvedKeywords.length,
      packedCount: packed.length,
      omittedByApprovalLimit: Math.max(0, allowed.length - approvedKeywords.length),
      omittedByByteBudget: Math.max(0, approvedKeywords.length - packed.length),
    },
  };
}
